import { useState, useMemo } from "react";
// routes
import Router from "./routes";
// theme
import ThemeConfig from "./theme";
import GlobalStyles from "./theme/globalStyles";
import { ThemeModeProvider } from "./theme/ThemeModeProvider";
// components
import ScrollToTop from "./components/ScrollToTop";
import { BaseOptionChartStyle } from "./components/charts/BaseOptionChart";
//  context
import { Context } from "./context";
import { MuiSnackBar } from "./components/@material-extend";
// DataGrid resizing styles
import "./styles/dataGridResize.css";

//mui components
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useSelector } from "react-redux";
import { useAuth } from "./hooks/useAuth.hook";
import { useUser } from "./hooks/useUser.hook";
import { TIMEZONES } from "./layouts/dashboard/TimezonePopover";
import { detectBrowserTimezone } from "./utils/detectBrowserTimezone";
import { Loader } from "./components/Loader";
import "src/styles/main.css";
// ----------------------------------------------------------------------

export default function App() {
  const [userDetail, setUserDetail] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [userTypeList, setUserTypeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timezone, setTimezone] = useState(() => {
    const stored = localStorage.getItem("zynka_selected_timezone");
    const storedMatch = TIMEZONES.find((tz) => tz.value === stored);
    if (storedMatch) return storedMatch;
    return detectBrowserTimezone(TIMEZONES);
  });
  const [messageOption, setMessageOption] = useState({
    open: false,
    message: "",
    variant: "success",
  });
  const { isAuthenticated: isLoggedIn } = useAuth();
  const { isFetching, isError } = useUser();
  const alert = useSelector(({ alert }) => alert);
  const userDetailMemo = useMemo(
    () => ({ userDetail, setUserDetail }),
    [userDetail, setUserDetail]
  );

  const authMemo = useMemo(
    () => ({ isAuthenticated, setIsAuthenticated }),
    [isAuthenticated, setIsAuthenticated]
  );

  const messageMemo = useMemo(
    () => ({ messageOption, setMessageOption }),
    [messageOption, setMessageOption]
  );

  const categoryMemo = useMemo(
    () => ({ categoryList, setCategoryList }),
    [categoryList, setCategoryList]
  );

  const userTypeMemo = useMemo(
    () => ({ userTypeList, setUserTypeList }),
    [userTypeList, setUserTypeList]
  );

  const loadingTypeMemo = useMemo(
    () => ({ loading, setLoading }),
    [loading, setLoading]
  );

  const setTimezoneWithPersistence = (option) => {
    localStorage.setItem("zynka_selected_timezone", option.value);
    setTimezone(option);
  };

  const timezoneMemo = useMemo(
    () => ({ timezone, setTimezone: setTimezoneWithPersistence }),
    [timezone]
  );

  return (
    <ThemeModeProvider>
      <Context.Provider
        value={{
          user: userDetailMemo,
          auth: authMemo,
          message: messageMemo,
          category: categoryMemo,
          userType: userTypeMemo,
          spinner: loadingTypeMemo,
          timezone: timezoneMemo,
        }}
      >
        <ThemeConfig>
          <ScrollToTop />
          <GlobalStyles />
          <BaseOptionChartStyle />
          {isFetching || isError ? (
            <Loader />
          ) : (
            <Router isLoggedIn={isLoggedIn} />
          )}

          <MuiSnackBar {...alert} />
          <Backdrop open={loading}>
            <CircularProgress />
          </Backdrop>
        </ThemeConfig>
      </Context.Provider>
    </ThemeModeProvider>
  );
}
